import React, { Fragment, useEffect, useRef, useState } from 'react';
import AOS from 'aos';
import styled from 'styled-components';
import breakpoints from 'utils/breakpoints';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import { getSalesCenterAddress, getSalesCenterMapHref } from 'utils/getMapLink';
import trackingEvents from 'utils/trackingEvents';
import { track } from 'utils/tracking';
import MapMarkerIcon from 'components/icons/MapMarker';
import CallIcon from 'components/icons/Call';
import MailIcon from 'components/icons/Mail';
import CalendarIcon from 'components/icons/Calendar';
import OpenNewTabIcon from 'components/icons/OpenNewTab';
import ChevronLeft from 'components/icons/ChevronLeft';
import tw from 'twin.macro';

const Overlay = styled.div`
  background: #00000073;
  z-index: 93;
`;

const FlayoutContainer = styled.div`
  left: 0;
  right: 0;
  width: 100%;
  z-index: 94;
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;

  &[data-aos='fade-left'] {
    transform: translate3d(100%, 0, 0);
  }

  &[data-aos='fade-left'].aos-animate {
    transform: translate3d(0, 0, 0);
  }

  &.is-closing {
    opacity: 0;
    transform: translate3d(100%, 0, 0) !important;
  }

  @media ${breakpoints.md} {
    left: auto;
    right: 0;
    max-width: 30rem;
  }
`;

const HoursGrid = styled.div`
  display: grid;
  grid-template-columns: 3rem 1fr;
  row-gap: 0.35rem;
  column-gap: 1.5rem;
`;

const AppointmentPanel = styled.div`
  border-radius: 0.625rem;
`;
const AppointmentPanelButton = styled.button`
  ${tw`h-12 rounded-md border-1-5 border-mattamy-blue bg-transparent px-4 font-trade-gothic-20 text-sm uppercase tracking-wide text-mattamy-blue hover:bg-mattamy-blue hover:text-white`}
`;

const getDayHours = (hours) => {
  if (hours) {
    const hoursSplit = hours.split('-');
    if (hoursSplit.length > 0 && hoursSplit[0].length > 1) return hours;
  }
  return '';
};

const hasHoursOrDisplayText = (day) =>
  day && (day.displayText || getDayHours(day.hours));

const getClosedHour = (schedule = []) => {
  const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const dayName = weekdays[new Date().getDay()];
  const hours = schedule.find((h) => h.day && h.day.includes(dayName));
  const closedHour = hours && hours.hours && hours.hours.split('-');
  return closedHour && closedHour.length > 1 ? closedHour[1].trim() : '';
};

const getOpenHour = (schedule = []) => {
  const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const dayName = weekdays[new Date().getDay()];
  const hours = schedule.find((h) => h.day && h.day.includes(dayName));
  const openHour = hours && hours.hours && hours.hours.split('-');
  return openHour && openHour.length > 0 ? openHour[0].trim() : '';
};

const getOpenStatus = (openNow, schedule = []) => {
  const closedHour = getClosedHour(schedule);
  const openHour = getOpenHour(schedule);

  if (openNow) {
    return closedHour ? `Closes at ${closedHour}` : '';
  }

  return openHour ? `Opens at ${openHour}` : '';
};

const handleAppointmentClick = (eventName, href) => {
  window.dataLayer.push({ event: eventName });
  track({ eventId: trackingEvents.BookAppointment });
  window.Calendly.initPopupWidget({ url: href });
};

const SubNavigationContactModal = ({
  isOpen,
  onClose,
  salesCenter: salesCenterData,
  scheduleAppointment,
  communityTitle,
}) => {
  const salesCenter = salesCenterData || {};
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const schedule = salesCenter.schedule || [];
  const address = getSalesCenterAddress(salesCenter);
  const mapHref = getSalesCenterMapHref(salesCenter);
  const phoneHref =
    salesCenter.phoneNumberCall &&
    salesCenter.phoneNumberCall.replace(/[^0-9+]/g, '');

  const handleClose = () => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
      setIsClosing(false);
      closeTimeoutRef.current = null;
    }, 500);
  };

  useOnClickOutside(modalRef, () => {
    handleClose();
  });

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setTimeout(() => AOS.refresh?.(), 0);
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    [],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Overlay className='fixed inset-0' />
      <FlayoutContainer
        className={`fixed h-screen top-0 overflow-y-auto bg-white ${
          isClosing ? 'is-closing' : ''
        }`}
        data-aos='fade-left'
        data-aos-duration='500'
        data-aos-once='true'
        ref={modalRef}
        aria-modal='true'
        role='dialog'
      >
        <header
          className='flex items-center px-6 border-b border-home-details-divider'
          style={{ height: '5.5rem' }}
        >
          <button
            type='button'
            className='mr-5 mb-1 text-mattamy-blue'
            onClick={handleClose}
          >
            <ChevronLeft className='text-mattamy-search-gray hover:text-mattamy-gray w-4 h-4' />
          </button>
          <h2 className='font-trade-gothic-20 text-3xl leading-none text-mattamy-blue'>
            Contact Us
          </h2>
        </header>
        <div className='p-6 text-mattamy-blue'>
          <h3 className='font-trade-gothic-20 text-2xl leading-tight mb-6'>
            {communityTitle || salesCenter.title}
            {salesCenter.city && ` - ${salesCenter.city}`}
          </h3>

          {address && (
            <a
              href={mapHref}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-start mb-8 font-graphie text-base leading-snug underline text-mattamy-blue'
            >
              <MapMarkerIcon className='mt-1 mr-3 h-4 w-4 text-action-blue flex-shrink-0' />
              <span>
                {address}
                <OpenNewTabIcon className='inline-block ml-1 h-4 w-4' />
              </span>
            </a>
          )}

          {salesCenter.phoneNumberCall && (
            <a
              href={`tel:${phoneHref}`}
              className='flex items-center mb-8 font-graphie text-base underline text-mattamy-blue'
              aria-label={'Call to ' + salesCenter.phoneNumberCall}
            >
              <CallIcon className='mr-3 h-4 w-4 text-action-blue flex-shrink-0' />
              {salesCenter.phoneNumberCall}
            </a>
          )}

          {salesCenter.email && (
            <a
              href={`mailto:${salesCenter.email}`}
              className='flex items-center mb-8 font-graphie text-base underline text-mattamy-blue break-all'
              aria-label={'Email to ' + salesCenter.email}
            >
              <MailIcon className='mr-3 h-4 w-4 text-action-blue flex-shrink-0' />
              {salesCenter.email}
            </a>
          )}

          {schedule.length > 0 && (
            <div className='mb-11'>
              <div className='flex items-center mb-3 font-graphie text-base'>
                <CalendarIcon className='mr-3 h-4 w-4 text-action-blue flex-shrink-0' />
                <span className='font-bold mr-1'>
                  {salesCenter.openNow ? 'Open Now' : 'Closed'}
                </span>
                {getOpenStatus(salesCenter.openNow, schedule) && (
                  <span className='mr-1'>{'\u00b7'}</span>
                )}
                <span>{getOpenStatus(salesCenter.openNow, schedule)}</span>
              </div>
              <HoursGrid className='ml-8 font-graphie text-sm'>
                {schedule.map((entry) => {
                  if (!entry || !entry.day || !hasHoursOrDisplayText(entry)) {
                    return null;
                  }

                  return (
                    <Fragment key={entry.day}>
                      <span>{entry.day.replace(':', '').substring(0, 3)}</span>
                      <span>
                        {entry.displayText
                          ? entry.displayText
                          : getDayHours(entry.hours)}
                      </span>
                    </Fragment>
                  );
                })}
              </HoursGrid>
            </div>
          )}

          {scheduleAppointment &&
            (scheduleAppointment.firstCtaLink ||
              scheduleAppointment.secondCtaLink) && (
              <AppointmentPanel className='bg-light-blue mt-10 p-6'>
                <h3 className='font-trade-gothic-20 text-xl leading-none text-center text-mattamy-blue mb-5'>
                  {scheduleAppointment.title}
                </h3>
                <div className='grid grid-cols-2 gap-2'>
                  {scheduleAppointment.firstCtaLink &&
                    scheduleAppointment.firstCtaLink.text &&
                    scheduleAppointment.firstCtaLink.href && (
                      <AppointmentPanelButton
                        onClick={() =>
                          handleAppointmentClick(
                            'schedule-appointment',
                            scheduleAppointment.firstCtaLink.href,
                          )
                        }
                      >
                        {scheduleAppointment.firstCtaLink.text}
                      </AppointmentPanelButton>
                    )}
                  {scheduleAppointment.secondCtaLink &&
                    scheduleAppointment.secondCtaLink.text &&
                    scheduleAppointment.secondCtaLink.href && (
                      <AppointmentPanelButton
                        onClick={() =>
                          handleAppointmentClick(
                            'schedule-appointment-virtual',
                            scheduleAppointment.secondCtaLink.href,
                          )
                        }
                      >
                        {scheduleAppointment.secondCtaLink.text}
                      </AppointmentPanelButton>
                    )}
                </div>
              </AppointmentPanel>
            )}
        </div>
      </FlayoutContainer>
    </>
  );
};

SubNavigationContactModal.defaultProps = {
  isOpen: false,
  onClose: () => {},
  salesCenter: {},
  scheduleAppointment: {},
  communityTitle: '',
};

export default SubNavigationContactModal;
